import { CdkConnectedOverlay, CdkOverlayOrigin } from '@angular/cdk/overlay';
import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';

import { PartnerDirectoryService, PartnerOption, PartnerServiceType } from '@lib/guarantees/data-access';
import { TranslatePipe } from '@ngx-translate/core';
import { catchError, combineLatest, debounceTime, of, switchMap } from 'rxjs';

import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';

import { OptionListComponent } from '../components/common/option-list.component';
import { OptionRowDirective } from '../components/common/option-row.directive';
import { OVERLAY_POSITIONS, SEARCH_DEBOUNCE_MS, SearchFieldComponent, fieldWidth } from '../components/common/search-field.component';

/**
 * Who delivers the service — the one question both ways of arranging a booking start from.
 *
 * What is typed narrows the list and is not the answer: the partner taken is shown in the field's
 * place once the box is empty again, so searching for another one never has to get past it.
 */
@Component({
  selector: 'guarantees-partner-picker',
  standalone: true,
  imports: [CdkConnectedOverlay, CdkOverlayOrigin, MatIcon, MatTooltip, TranslatePipe, OptionListComponent, OptionRowDirective, SearchFieldComponent],
  // The field inside measures itself to size the list, so the host has to take a width of its own.
  host: { class: 'block' },
  template: `
    <guarantees-search-field
      cdkOverlayOrigin
      #origin="cdkOverlayOrigin"
      icon="business"
      name="partnerSearch"
      [ariaLabel]="'guaranteeV2.label.partner' | translate"
      [placeholder]="display() || ('guaranteeV2.message.searchPartner' | translate)"
      [filled]="!!display()"
      [value]="term()"
      [loading]="loading()"
      (valueChange)="type($event)"
      (opened)="unfold()"
      (closed)="close()"></guarantees-search-field>

    <!-- In an overlay, not in the flow: a list inside a dialog is cut off by the dialog's own scroll. -->
    <ng-template
      cdkConnectedOverlay
      [cdkConnectedOverlayOrigin]="origin"
      [cdkConnectedOverlayOpen]="open()"
      [cdkConnectedOverlayWidth]="panelWidth()"
      [cdkConnectedOverlayPositions]="overlayPositions"
      (overlayOutsideClick)="close()">
      <guarantees-option-list [empty]="partners().length === 0 && !loading()" [emptyMessage]="'guaranteeV2.message.noPartners' | translate">
        @for (option of partners(); track option.id) {
          <li>
            <button guaranteesOption type="button" [class]="option.id === selected()?.id ? 'bg-primary-50' : ''" (click)="choose(option)">
              <span class="min-w-0 flex-1 truncate text-neutral-700">{{ option.name }}</span>

              <!-- What the partner's integration decides is asked elsewhere, so it is said here. -->
              @if (option.integrationEnabled) {
                <mat-icon class="h-4! w-4! shrink-0 text-base! leading-4! text-success-500" [matTooltip]="'guaranteeV2.message.partnerIntegrated' | translate">bolt</mat-icon>
              }
            </button>
          </li>
        }
      </guarantees-option-list>
    </ng-template>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PartnerPickerComponent {
  service = input.required<PartnerServiceType>();
  /** The partner already named, so re-entering the step shows the answer rather than a blank. */
  selected = input<PartnerOption | undefined>(undefined);

  chosen = output<PartnerOption>();

  private readonly directory = inject(PartnerDirectoryService);

  protected readonly overlayPositions = OVERLAY_POSITIONS;

  private readonly origin = viewChild.required(CdkOverlayOrigin);

  protected readonly term = signal('');
  protected readonly open = signal(false);
  protected readonly loading = signal(false);
  protected readonly partners = signal<PartnerOption[]>([]);

  /**
   * Measured when the list opens rather than read off the element in the template: a field inside a
   * dialog still being laid out measures zero, which the overlay reads as no width at all.
   */
  protected readonly panelWidth = signal(0);

  protected readonly display = computed(() => this.selected()?.name ?? '');

  constructor() {
    // Paired rather than nested: the list is filtered by the service, so the first fetch waits for
    // that input to bind, and every later one is driven by what has been typed.
    combineLatest([toObservable(this.service), toObservable(this.term)])
      .pipe(
        debounceTime(SEARCH_DEBOUNCE_MS),
        switchMap(([service, term]) => {
          this.loading.set(true);

          return this.directory.searchPartners(service, term).pipe(catchError(() => of<PartnerOption[]>([])));
        }),
        takeUntilDestroyed()
      )
      .subscribe(partners => {
        this.partners.set(partners);
        this.loading.set(false);
      });
  }

  /** Every way the list opens comes through here, so it is never shown before it is sized. */
  protected unfold(): void {
    this.panelWidth.set(fieldWidth(this.origin()));
    this.open.set(true);
  }

  protected type(value: string): void {
    this.term.set(value);
    this.unfold();
  }

  protected choose(partner: PartnerOption): void {
    this.term.set('');
    this.open.set(false);
    this.chosen.emit(partner);
  }

  /**
   * The box holds the query while the list is open, and the answer once it is not: a term left
   * standing behind a closed list narrows nothing and covers the partner actually chosen.
   */
  protected close(): void {
    this.term.set('');
    this.open.set(false);
  }
}
